import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { FileText, Download, ExternalLink, FileType2, Link2, Check } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, ErrorBox, PageHeader, QueryState } from '../components/charts';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import config from '../config';
import { formatDateLocale } from '../lib/format';

const fmtSize = (b) => b == null ? '' : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`;

// Touch-sized on phones, compact on desktop.
const BTN_SOLID = 'inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-50';
const BTN_OUTLINE = 'inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:border-[#cf9a40] disabled:opacity-50';

export default function Reports() {
  const t = useT();
  const rl = useApi(() => api.reportsList(), []);
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState('');
  const [actionErr, setActionErr] = useState(null);

  // Public "anyone with the link" share URL (no login). Always absolute so it
  // can be pasted into an email; uses the API origin when one is configured.
  const shareToken = rl.data?.share_token;
  let shareBase = config.apiUrl || '';
  if (!shareBase || shareBase === '/') shareBase = window.location.origin;
  shareBase = shareBase.replace(/\/+$/, '');
  const shareUrl = (key) => `${shareBase}/api/analytics/share/${key}/?t=${shareToken}`;

  // Fallback viewer (no share token): open the tab synchronously, before the
  // await, so iOS Safari / popup blockers don't swallow it.
  const openReport = async (key) => {
    const w = window.open('', '_blank');
    setBusy(key + ':open'); setActionErr(null);
    try {
      const blob = await api.reportBlob(key, false);
      const url = URL.createObjectURL(blob);
      if (w) w.location = url; else window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { w?.close(); setActionErr(e); }
    finally { setBusy(''); }
  };
  const download = async (key, filename) => {
    setBusy(key + ':dl'); setActionErr(null);
    try {
      const blob = await api.reportBlob(key, true);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setActionErr(e); }
    finally { setBusy(''); }
  };
  const copyShare = async (key) => {
    try { await navigator.clipboard.writeText(shareUrl(key)); } catch { /* clipboard may be blocked */ }
    setCopied(key); setTimeout(() => setCopied(''), 2000);
  };

  // "Bekijk" is a plain link to the public share URL — reliable on phones.
  const ViewBtn = ({ k, label, icon: Icon, color }) => shareToken ? (
    <a href={shareUrl(k)} target="_blank" rel="noreferrer" className={`${BTN_SOLID} ${color}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </a>
  ) : (
    <button onClick={() => openReport(k)} disabled={busy.startsWith(k)} className={`${BTN_SOLID} ${color}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
  const DownloadBtn = ({ k, filename }) => (
    <button onClick={() => download(k, filename)} disabled={busy.startsWith(k)} className={BTN_OUTLINE}>
      <Download className="h-3.5 w-3.5" /> {t('Download')}
    </button>
  );
  const ShareBtn = ({ k }) => !shareToken ? null : (
    <button onClick={() => copyShare(k)} title={shareUrl(k)} className={BTN_OUTLINE}>
      {copied === k ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> {t('Gekopieerd')}</> : <><Link2 className="h-3.5 w-3.5" /> {t('Kopieer link')}</>}
    </button>
  );

  const isEmpty = (d) => !d.guide && !(d.reports || []).some((r) => r.html || r.pdf);

  return (
    <PageShell page="Rapporten" accent="gold">
      <PageHeader title={t('Rapporten')}
        subtitle={t('De volledige data-analyse als slide-rapport — bekijk online of download om te delen.')} />

      {actionErr && <div className="mb-4"><ErrorBox error={actionErr} /></div>}

      <QueryState q={rl} isEmpty={isEmpty} empty={t('Nog geen rapporten gegenereerd.')}>
        {({ reports = [], guide }) => (
          <>
            {guide && (
              <SectionCard className="mb-4" title={t('Handleiding')}
                subtitle={t('Hoe gebruik je het Perfy-platform — uitleg per onderdeel (in het Frans)')}>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <ViewBtn k={guide.key} label={t('Bekijk de gids')} icon={ExternalLink} color="bg-[#114817] hover:bg-[#0d3a13]" />
                  <DownloadBtn k={guide.key} filename="guide_perfy_fr.html" />
                  <ShareBtn k={guide.key} />
                  <span className="text-[11px] text-slate-400">{fmtSize(guide.size)}</span>
                </div>
              </SectionCard>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reports.map((r) => (
                <SectionCard key={r.lang}
                  title={`${t('Data-analyse')} · ${r.label}`}
                  subtitle={r.html?.generated ? `${t('Gegenereerd op')} ${formatDateLocale(r.html.generated)}` : t('Nog niet gegenereerd')}>
                  <div className="flex items-start gap-3 pt-1">
                    <div className="hidden sm:block rounded-xl bg-[#114817]/8 p-3"><FileText className="h-6 w-6 text-[#114817]" /></div>
                    <div className="flex-1 space-y-2">
                      {r.html ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 w-10">HTML</span>
                          <ViewBtn k={r.html.key} label={t('Bekijk')} icon={ExternalLink} color="bg-[#114817] hover:bg-[#0d3a13]" />
                          <DownloadBtn k={r.html.key} filename={`calum_report_${r.lang}.html`} />
                          <ShareBtn k={r.html.key} />
                          <span className="text-[11px] text-slate-400">{fmtSize(r.html.size)}</span>
                        </div>
                      ) : <p className="text-xs text-slate-400">{t('HTML nog niet beschikbaar.')}</p>}

                      {r.pdf ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 w-10">PDF</span>
                          <ViewBtn k={r.pdf.key} label={t('Bekijk')} icon={FileType2} color="bg-[#cf9a40] hover:bg-[#b9882f]" />
                          <DownloadBtn k={r.pdf.key} filename={`calum_report_${r.lang}.pdf`} />
                          <ShareBtn k={r.pdf.key} />
                          <span className="text-[11px] text-slate-400">{fmtSize(r.pdf.size)}</span>
                        </div>
                      ) : <p className="text-xs text-slate-400">{t('PDF nog niet beschikbaar.')}</p>}
                    </div>
                  </div>
                </SectionCard>
              ))}
            </div>
          </>
        )}
      </QueryState>

      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed max-w-2xl">
        {t('Deel via de knop "Kopieer link" of download de PDF.')}{' '}
        {t('Het rapport bevat bedrijfscijfers — deel het alleen met wie het mag zien.')}
      </p>
    </PageShell>
  );
}
